/**
 * OwnerStatisticsService
 * 오너 통계 비즈니스 로직
 */
import type {
  TimeSlotStats,
  TimeSlotChartData,
  PlanStats,
  PlanChartData,
} from '../models/OwnerStatistics';

/**
 * OwnerStatisticsService 클래스
 * 통계 관련 비즈니스 로직 및 유틸리티 함수
 */
export class OwnerStatisticsService {
  /**
   * 차트 색상 팔레트
   */
  readonly CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  /**
   * 금액 포맷팅 (만원 단위)
   */
  formatCurrency(amount: number): string {
    return Math.round(amount / 10000).toLocaleString() + '만원';
  }

  /**
   * 숫자 포맷팅 (천 단위 구분)
   */
  formatNumber(num: number): string {
    return num.toLocaleString();
  }

  /**
   * 퍼센트 포맷팅
   */
  formatPercent(rate: number): string {
    return `${rate >= 0 ? '+' : ''}${rate.toFixed(1)}%`;
  }

  /**
   * 성장률 색상 클래스 반환
   */
  getGrowthColorClass(rate: number): string {
    if (rate > 0) return 'text-green-600';
    if (rate < 0) return 'text-red-600';
    return 'text-gray-600';
  }

  /**
   * 성장률 아이콘 타입 반환
   */
  getGrowthIconType(rate: number): 'up' | 'down' | 'neutral' {
    if (rate > 0) return 'up';
    if (rate < 0) return 'down';
    return 'neutral';
  }

  /**
   * 시간대별 차트 데이터 변환
   */
  convertTimeSlotChartData(timeSlots: TimeSlotStats[]): TimeSlotChartData[] {
    return timeSlots.map((slot) => ({
      hour: `${slot.hour}시`,
      이용건수: slot.usage_count,
      매출: slot.revenue / 10000, // 만원 단위
      점유율: slot.occupancy_rate,
    }));
  }

  /**
   * 요금제별 차트 데이터 변환
   */
  convertPlanChartData(plans: PlanStats[]): PlanChartData[] {
    return plans.map((plan) => ({
      name: plan.plan_name,
      value: plan.usage_count,
      revenue: plan.revenue,
      percentage: plan.percentage,
    }));
  }

  /**
   * 평균 세션 시간 포맷팅 (분 → 시간:분)
   */
  formatSessionDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}시간 ${mins}분`;
  }

  /**
   * 피크 타임 포맷팅
   */
  formatPeakHours(hours: number[]): string {
    if (!hours || hours.length === 0) return '-';
    return hours.map((h) => `${h}시`).join(', ');
  }

  /**
   * 날짜 포맷팅 (ISO → YYYY-MM-DD HH:MM)
   */
  formatDateTime(dateString: string): string {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  }

  /**
   * 점유율 색상 클래스 반환
   */
  getOccupancyColorClass(rate: number): string {
    if (rate >= 90) return 'text-red-600';
    if (rate >= 70) return 'text-orange-600';
    if (rate >= 50) return 'text-yellow-600';
    return 'text-green-600';
  }

  /**
   * 기간 라벨 반환
   */
  getPeriodLabel(days: number): string {
    const labels: Record<number, string> = {
      7: '최근 7일',
      30: '최근 30일',
      90: '최근 90일',
    };
    return labels[days] || `최근 ${days}일`;
  }
}

/**
 * OwnerStatisticsService 싱글톤 인스턴스
 */
export const ownerStatisticsService = new OwnerStatisticsService();
